import { EXAM_EVENT_DATA_TEST_ID } from '@e2e/constants/exam-center';
import {
  ExamDateReadonlyField,
  ExamTimeFormFields,
  ExamTimeReadonlyFields,
  type ExamTimeFormFieldsProps,
  type ExamTimeReadonlyFieldsProps,
} from '@exam-center/exam-event-management';
import { Box, Flex, FormDateInput, FormDateTimeInput, Grid, Text } from '@shared/components';
import { dayjs } from '@shared/utils';

import { type RoomData } from '../../containers';

// Type converter function with overloads
function convertExamTimeProps(
  slotProps: ExamScheduleFormFieldProps['slotProps'],
  isFormFields: true,
): { slotProps: ExamTimeFormFieldsProps['slotProps'] };
function convertExamTimeProps(
  slotProps: ExamScheduleFormFieldProps['slotProps'],
  isFormFields: false,
): ExamTimeReadonlyFieldsProps;
function convertExamTimeProps(
  slotProps: ExamScheduleFormFieldProps['slotProps'],
  isFormFields: boolean,
) {
  if (isFormFields) {
    return {
      slotProps: slotProps as ExamTimeFormFieldsProps['slotProps'],
    };
  }

  return {
    examDate: slotProps?.examDate,
    sessions: slotProps?.sessions,
  } as ExamTimeReadonlyFieldsProps;
}

export interface ExamScheduleFormFieldProps {
  slotProps: {
    examDate: {
      isExamDateSelected?: boolean;
      date?: Date;
      eventStartDate?: Date;
      eventEndDate?: Date;
      minDate: Date;
      maxDate: Date;
      examStartedAt?: Date;
      examEndedAt?: Date;
    };
    province: {
      isProvinceSelected: boolean;
    };
    name: {
      isNameSelected: boolean;
    };
    room: {
      roomData: RoomData[];
    };
  } & (ExamTimeFormFieldsProps['slotProps'] | ExamTimeReadonlyFieldsProps);
  isExamDateEditable?: boolean;
}

export default function ExamScheduleFormField(props: Readonly<ExamScheduleFormFieldProps>) {
  const { slotProps, isExamDateEditable = true } = props;
  const { examDate } = slotProps ?? {};

  const examMinDate = dayjs.max(dayjs(examDate?.minDate ?? new Date()), dayjs()).toDate();

  const eventEndMinDate = dayjs
    .max(dayjs(examDate?.eventStartDate ?? new Date()), dayjs())
    .toDate();

  return (
    <Grid
      sx={(theme) => ({
        columnGap: theme.spacing(6),
        rowGap: theme.spacing(6),
        gridTemplateColumns: '240px 1fr',
      })}
    >
      <Text variant='body1Semibold'>วันที่สอบ</Text>
      {isExamDateEditable ? (
        <Box sx={{ maxWidth: '200px' }}>
          <FormDateInput
            name='examDate.date'
            inputFormat='DD-MM-YYYY'
            placeholder='DD-MM-YYYY'
            label='วันที่สอบ'
            data-testid={EXAM_EVENT_DATA_TEST_ID.EXAM_DATE_INPUT}
            minDate={examMinDate}
            maxDate={examDate?.maxDate}
            initialMonth={examMinDate}
            required
          />
        </Box>
      ) : (
        <ExamDateReadonlyField examDate={examDate} />
      )}
      <Text variant='body1Semibold'>เวลาสอบทั้งหมด</Text>
      {isExamDateEditable ? (
        <ExamTimeFormFields {...convertExamTimeProps(slotProps, true)} />
      ) : (
        <ExamTimeReadonlyFields {...convertExamTimeProps(slotProps, false)} />
      )}
      <Text variant='body1Semibold'>วันที่เปิด-ปิดรับสมัคร</Text>
      <Flex sx={{ gap: (theme) => theme.spacing(2), alignItems: 'flex-start' }}>
        <FormDateTimeInput
          name='eventStartDate'
          label='วันที่เปิดรับสมัคร'
          data-testid={EXAM_EVENT_DATA_TEST_ID.REGISTRATION_START_DATE_INPUT}
          inputFormat='DD-MM-YYYY'
          placeholder='DD-MM-YYYY'
          disabled={!examDate.isExamDateSelected}
          minDate={new Date()}
          maxDate={examDate.eventEndDate ?? examDate.date}
          defaultTime={{
            hour: 0,
            minute: 0,
            second: 0,
          }}
          rules={{
            required: true,
          }}
        />
        <Text sx={{ p: (theme) => theme.spacing(8.5, 1, 2.5, 1) }}>ถึง</Text>
        <FormDateTimeInput
          name='eventEndDate'
          label='วันที่ปิดรับสมัคร'
          data-testid={EXAM_EVENT_DATA_TEST_ID.REGISTRATION_END_DATE_INPUT}
          inputFormat='DD-MM-YYYY'
          placeholder='DD-MM-YYYY'
          disabled={!examDate?.isExamDateSelected}
          minDate={eventEndMinDate}
          maxDate={examDate?.date}
          initialMonth={eventEndMinDate}
          rules={{
            required: true,
          }}
        />
      </Flex>
    </Grid>
  );
}
